// gsap-scrolltrigger-smashballoon-bridge.lazy.js
// Loads GSAP/ScrollTrigger ONLY if an Instagram feed exists (or appears later).

( () => {
  const FEED_SELECTORS = ['.sbi', '#sb_instagram', '.sb_instagram'];
  const findFeeds = ( root = document ) =>
    FEED_SELECTORS.flatMap( ( sel ) => Array.from( root.querySelectorAll( sel ) ) );

  // Memoized dynamic import so we only load GSAP once.
  let gsapReadyPromise = null;
  const ensureGSAP = () => {
    if ( gsapReadyPromise ) return gsapReadyPromise;
    gsapReadyPromise = import( 'gsap' ).then( async ( mod ) => {
      const gsap = mod.default || mod.gsap || mod;
      const {ScrollTrigger} = await import( 'gsap/ScrollTrigger.js' );
      gsap.registerPlugin( ScrollTrigger );
      return {gsap, ScrollTrigger};
    } );
    return gsapReadyPromise;
  };

  // Debounced refresh using rAF
  let rafId;
  const refreshWith = ( {ScrollTrigger} ) => {
    if ( rafId ) cancelAnimationFrame( rafId );
    rafId = requestAnimationFrame( () => ScrollTrigger.refresh() );
  };

  const observed = new WeakSet();

  // Attach observers to a feed element
  const observeFeed = async ( el ) => {
    if ( !el || observed.has( el ) ) return;
    observed.add( el );

    const api = await ensureGSAP();

    // 1) ResizeObserver: when the feed box size changes (images, layout, load more)
    if ( 'ResizeObserver' in window ) {
      const ro = new ResizeObserver( () => refreshWith( api ) );
      ro.observe( el );
    }

    // 2) Image loads (lazy)
    el.querySelectorAll( 'img' ).forEach( ( img ) => {
      if ( !img.complete ) {
        img.addEventListener( 'load', () => refreshWith( api ), {once: true} );
      }
    } );

    // 3) Catch "Load More" / pagination clicks
    el.addEventListener(
        'click',
        ( e ) => {
          const t = e.target;
          if (
            t &&
          ( t.matches( '.sbi_load_btn, .sbi_load_more' ) ||
            ( t.closest && t.closest( '.sbi_load_btn, .sbi_load_more' ) ) )
          ) {
            setTimeout( () => refreshWith( api ), 150 ); // allow DOM append
          }
        },
        true,
    );

    // First-time nudge after attaching
    refreshWith( api );
  };

  // Initialize any feeds already in the DOM
  const initExistingFeeds = () => {
    const feeds = findFeeds();
    if ( !feeds.length ) return false;
    feeds.forEach( observeFeed );
    return true;
  };

  // Start: see if feeds are already present
  const hadFeedsInitially = initExistingFeeds();

  // Also watch for feeds injected later (shortcode render, AJAX, SPA nav, etc.)
  const mo = new MutationObserver( ( muts ) => {
    for ( const m of muts ) {
      m.addedNodes.forEach( ( n ) => {
        if ( !( n instanceof HTMLElement ) ) return;

        // If a feed node itself was added
        if ( FEED_SELECTORS.some( ( sel ) => n.matches?.( sel ) ) ) {
          observeFeed( n );
        }

        // Or if feeds exist within the added subtree
        const innerFeeds = findFeeds( n );
        if ( innerFeeds.length ) innerFeeds.forEach( observeFeed );
      } );
    }
  } );
  mo.observe( document.documentElement, {childList: true, subtree: true} );

  // Safety nets: if feeds were there at load, do one refresh after everything settles
  if ( hadFeedsInitially ) {
    window.addEventListener( 'load', async () => {
      const api = await ensureGSAP();
      refreshWith( api );
    } );
    window.addEventListener( 'resize', async () => {
      const api = await ensureGSAP();
      refreshWith( api );
    } );
  }
} )();
